import { type ReactNode, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useStoreDetailV1StoresPlatformStoreIdGet } from "../api/generated/qcsControlPlane";
import type { StoreEvent } from "../api/generated/model";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { DataBody } from "../components/JsonPanel";
import { StoreDetailMap } from "../components/StoreDetailMap";
import { StoreExclusion } from "../components/StoreExclusion";
import {
  type LatLon,
  type Platform,
  type StoreCell,
  ago,
  fmtDateTime,
  fmtInt,
  platformLabel,
  storeStatusLabel,
  storeStatusTone,
} from "../api/labels";
import { WithRoutePlatform } from "../components/UnknownPlatform";
import { AnchorKindPill, TierPills } from "../components/tiers";
import {
  Badge,
  ErrorState,
  PageHeader,
  Panel,
  Skeleton,
  SkeletonRegion,
  Stack,
} from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Kpi } from "../components/foundation";
import { AnimatedNumber } from "../lib/motion";

// Per-store detail page: every field the merchant sweep persists for one dark
// store, a focused map of its serviceable area + probe points + anchor, and its
// lifecycle timeline. Reached from the Stores table (store id -> this page).
export function StoreDetail() {
  const { platform = "", storeId = "" } = useParams<{
    platform: string;
    storeId: string;
  }>();
  return (
    <WithRoutePlatform platform={platform}>
      {(key) => <StoreDetailView platform={key} storeId={storeId} />}
    </WithRoutePlatform>
  );
}

function StoreDetailView({ platform, storeId }: { platform: Platform; storeId: string }) {
  useBreadcrumbTail([{ label: storeId }]);

  const q = useStoreDetailV1StoresPlatformStoreIdGet(platform, encodeURIComponent(storeId), {
    query: { refetchInterval: 30000, retry: false },
  });

  // Lifecycle events: filter by event type, sort by time; transition + run are
  // rendered but not sortable (they read as one-off references, not orderings).
  const eventColumns = useMemo<AnyColumnDef<StoreEvent>[]>(() => {
    const col = createColumnHelper<StoreEvent>();
    return [
      col.accessor((r) => r.event_type, {
        id: "event",
        header: "Event",
        cell: ({ row }) => (
          <Badge variant={toneVariant(eventTone(row.original.event_type))} style={{ fontSize: 10.5 }}>
            {row.original.event_type}
          </Badge>
        ),
        meta: { width: 140, filterSelect: true, filterLabel: "Event" },
      }),
      col.accessor((r) => `${r.prev_status || "∅"} → ${r.new_status || "∅"}`, {
        id: "transition",
        header: "Transition",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11 }}>
            {row.original.prev_status || "∅"} → {row.original.new_status || "∅"}
          </span>
        ),
        meta: { minWidth: 150 },
        enableSorting: false,
      }),
      col.accessor((r) => r.event_at, {
        id: "when",
        header: "When",
        cell: ({ row }) => <span className="faint">{fmtDateTime(row.original.event_at)}</span>,
        meta: { minWidth: 180, exportValue: (r) => r.event_at },
      }),
      col.accessor((r) => r.run_id, {
        id: "run",
        header: "Run",
        cell: ({ row }) => runLink(row.original.run_id),
        meta: { width: 110 },
        enableSorting: false,
      }),
    ];
  }, []);

  const head = (
    <PageHeader
      className="mb-3.5"
      title={storeId}
      titleClassName="font-mono"
      titleMeta={
        <>
          {q.data && (
            <Badge variant={toneVariant(storeStatusTone(q.data.status))}>
              {storeStatusLabel(q.data.status)}
            </Badge>
          )}
          {/* Beside the lifecycle pill, never instead of it: one says whether the store is
              open, the other whether we count what it reports. */}
          {q.data?.is_excluded && <Badge variant="outline">Excluded</Badge>}
        </>
      }
      description={
        <>
          {platformLabel(platform)} dark store
          {q.data?.name ? ` · ${q.data.name}` : ""}
        </>
      }
    />
  );

  if (q.isError) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load store"
              message={
                <>
                  Store {storeId} may not be in the current {platformLabel(platform)} dimension
                  (wiped, or never discovered).
                </>
              }
              onRetry={() => q.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }
  if (!q.data) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <SkeletonRegion label="Loading store">
              <Stack gap="sm" className="p-4">
                <Skeleton h={28} />
                <Skeleton h={28} />
                <Skeleton h={28} />
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      </>
    );
  }

  const s = q.data;
  const anchor: [number, number] = [s.latitude, s.longitude];
  // The routing probe point (Phase 2). Unset (0,0) for stores not yet re-swept since the split
  // -> no probe. Otherwise we pass it through even when it coincides with the store location (an
  // exclusive store) so the map can show that coincidence rather than silently dropping it.
  const probeSet = s.probe_latitude !== 0 || s.probe_longitude !== 0;
  const probeCoincident =
    probeSet && s.probe_latitude === s.latitude && s.probe_longitude === s.longitude;
  const probe: [number, number] | null = probeSet
    ? [s.probe_latitude, s.probe_longitude]
    : null;
  const eta = s.eta_minutes == null ? "—" : `${s.eta_minutes} min`;
  const cost = s.cost == null ? "—" : `₹${s.cost}`;
  const fcost = s.fulfilment_cost == null ? "—" : `₹${s.fulfilment_cost}`;

  return (
    <>
      {head}

      {/* at-a-glance KPIs */}
      <div className="grid g-4 mb-3.5">
        <Kpi label="status" value={storeStatusLabel(s.status)} tone={storeStatusTone(s.status)} />
        <Kpi label="ETA" value={eta} />
        <Kpi label="tiers" value={<TierPills tiers={s.tiers} fallback={s.tier} />} small />
        <Kpi label="missed sweeps" value={<AnimatedNumber value={s.missed_sweeps} format={fmtInt} />} tone={s.missed_sweeps > 0 ? "warn" : undefined} />
      </div>

      <StoreExclusion
        platform={platform}
        storeId={s.store_id}
        isExcluded={s.is_excluded ?? false}
        reason={s.excluded_reason ?? ""}
        excludedAt={s.excluded_at ?? ""}
        onChanged={() => void q.refetch()}
      />

      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(12,1fr)",
          marginBottom: 14,
          alignItems: "start",
        }}
      >
        {/* The merged details panel is tall, so the map and the timeline stack BESIDE it
            in one column instead of leaving a screen of white space under the map. */}
        <div className="col" style={{ gridColumn: "span 7", gap: 14, minWidth: 0 }}>
        {/* map */}
        <Panel>
          <Panel.Head title="Serviceable area">
            <div className="flex-1" />
            <span className="faint text-[11px]">
              {s.cells.length} probe {s.cells.length === 1 ? "point" : "points"}
              {s.boundary ? " · hull" : ""}
            </span>
          </Panel.Head>
          <Panel.Body tight>
            <StoreDetailMap
              height={620}
              platform={platform}
              storeId={s.store_id}
              anchor={anchor}
              probe={probe}
              boundary={s.boundary as LatLon[] | null}
              cells={s.cells as StoreCell[]}
            />
            <span className="hint mt-1.5">
              The ringed logo is the store's location (representative centre). The dots are the
              serviceable probe points where it was found, coloured by the delivery tier it served
              at that point (see legend); the dashed hull is its serviceable-area boundary. Switch{" "}
              <em>Colour by</em> to <em>Overlap</em> to shade each probe point by how many stores
              serve it (green = exclusive to this store, redder = more contested) - hover a point to
              see which stores share it.
              {probeCoincident
                ? " The black-and-yellow dashed ring around the logo is the routing probe a flow plants to read this store: for this store it sits exactly on the store location."
                : probe
                  ? " The yellow dot joined by a line is the routing probe point a flow plants to read this store."
                  : ""}
            </span>
          </Panel.Body>
        </Panel>

        {/* lifecycle timeline for this store */}
        <Panel>
          <Panel.Head title="Lifecycle timeline">
            <div className="flex-1" />
            <span className="num faint text-[11px]">{s.events.length} events</span>
          </Panel.Head>
          <Panel.Body tight>
            <DataTable
              tableKey="store-events"
              columns={eventColumns}
              data={s.events}
              getRowId={(e, i) => `${e.event_at}-${e.event_type}-${e.run_id}-${i}`}
              enableGlobalFilter={false}
              csvFilename={`store-${s.store_id}-events`}
              isLoading={q.isLoading}
              emptyMessage="No lifecycle events for this store."
            />
          </Panel.Body>
        </Panel>
        </div>

        {/* detail fields */}
        <Panel style={{ gridColumn: "span 5" }}>
          <Panel.Head title="Store details">
            <div className="flex-1" />
            <Link
              to={`/stores?platform=${platform}&focus=${encodeURIComponent(storeId)}`}
              className="as-link"
              style={{ fontSize: 12 }}
              title="Open the stores map zoomed to this store, among its neighbours"
            >
              Show on store map →
            </Link>
          </Panel.Head>
          <Panel.Body tight>
            <div className="panel-sub">Details</div>
            <table className="tbl">
              <tbody>
                <Row k="Store ID" v={<span className="mono">{s.store_id}</span>} />
                <Row k="Platform" v={platformLabel(s.platform)} />
                <Row k="Name" v={s.name || dash} />
                <Row k="Address" v={s.formatted_address || s.address || dash} />
                <Row k="Locality" v={s.locality || dash} />
                <Row k="City" v={s.city || dash} />
                <Row k="State" v={s.state || dash} />
                <Row k="Pincode" v={s.pincode ? <span className="mono">{s.pincode}</span> : dash} />
                <Row k="Location lat" v={<span className="mono">{s.latitude.toFixed(6)}</span>} />
                <Row k="Location lon" v={<span className="mono">{s.longitude.toFixed(6)}</span>} />
                {probe && (
                  <Row
                    k="Probe point"
                    v={
                      <span className="mono">
                        {probe[0].toFixed(6)}, {probe[1].toFixed(6)}
                        {probeCoincident && (
                          <span className="faint"> (same as store location)</span>
                        )}
                      </span>
                    }
                  />
                )}
                <Row k="Anchor kind" v={<AnchorKindPill kind={s.anchor_kind} />} />
                {s.always_shadowed ? (
                  <Row k="Always shadowed" v={<Badge variant="outline-warning">Yes, never wins routing (cascade-only)</Badge>} />
                ) : null}
                <Row k="Serviceability" v={s.serviceability_type || dash} />
                <Row k="Order types" v={s.order_types.length ? s.order_types.join(", ") : dash} />
                <Row k="Primary order type" v={s.primary_order_type || dash} />
                <Row k="Nightly store" v={s.is_nightly_store == null ? dash : s.is_nightly_store ? "Yes" : "No"} />
                <Row k="Cost" v={cost} />
                <Row k="Fulfilment cost" v={fcost} />
              </tbody>
            </table>

            <div className="panel-sub">Provenance</div>
            <table className="tbl">
              <tbody>
                <Row k="First seen" v={`${fmtDateTime(s.first_seen_at)} \u00b7 ${ago(s.first_seen_at)}`} />
                <Row k="Last seen" v={`${fmtDateTime(s.last_seen_at)} \u00b7 ${ago(s.last_seen_at)}`} />
                <Row k="Updated" v={fmtDateTime(s.updated_at)} />
                <Row k="First seen run" v={runLink(s.first_seen_run_id)} />
                <Row k="Last seen run" v={runLink(s.last_seen_run_id)} />
                <Row k="Geocode source" v={s.geo_source || dash} />
                {s.formatted_address && s.address && s.formatted_address !== s.address && (
                  <Row k="Platform address" v={s.address} />
                )}
              </tbody>
            </table>

            {/* platform-reported detail (ETA breakdown / serviceability extras / geofence),
                folded in as a sub-section instead of three loose JSON cards */}
            {(s.eta_map || s.geofence || s.extra) && (
              <>
                <div className="panel-sub">Platform data</div>
                {/* one scroll frame for the group: these blobs are unbounded (a store with
                    nine ETA tiers runs a full screen) and would otherwise stretch this
                    column far past the map beside it */}
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                {s.eta_map && (
                  <>
                    <div className="faint" style={{ fontSize: 11, margin: "6px 0 4px" }}>ETA map</div>
                    <DataBody raw={s.eta_map} />
                  </>
                )}
                {s.extra && (
                  <>
                    <div className="faint" style={{ fontSize: 11, margin: "10px 0 4px" }}>Extra</div>
                    <DataBody raw={s.extra} />
                  </>
                )}
                {s.geofence && (
                  <>
                    <div className="faint" style={{ fontSize: 11, margin: "10px 0 4px" }}>Geofence</div>
                    <DataBody raw={s.geofence} />
                  </>
                )}
                </div>
              </>
            )}
          </Panel.Body>
        </Panel>
      </div>

    </>
  );
}

const dash = <span className="faint">—</span>;

function runLink(id: string): ReactNode {
  if (!id) return dash;
  return <Link className="mono" to={`/runs/${id}`} style={{ fontSize: 11 }}>{id.slice(0, 8)}</Link>;
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <tr>
      <td className="faint" style={{ whiteSpace: "nowrap", width: 1, paddingRight: 18 }}>{k}</td>
      <td className="strong" style={{ fontSize: 12.5 }}>{v}</td>
    </tr>
  );
}

function eventTone(t: string): "ok" | "warn" | "info" | "idle" {
  if (t === "added") return "ok";
  if (t === "reactivated") return "info";
  if (t === "removed") return "warn";
  return "idle";
}
